import React, { useEffect, useMemo, useRef, useState } from "react";
import { Box, Collapse, Typography } from "@mui/material";
import { AccessTime as ClockIcon, ChevronRight as ChevronRightIcon } from "@mui/icons-material";
import { COLORS } from "../../styles/uiConstants";

const pad2 = (n) => String(n).padStart(2, "0");

const DEFAULT_START = "08:00";
const DEFAULT_END = "18:00";

/** Tüm 15 dk aralıklı saat seçenekleri */
const ALL_SLOTS = (() => {
  const opts = [];
  for (let h = 0; h < 24; h++) {
    for (let m = 0; m < 60; m += 15) {
      opts.push(`${pad2(h)}:${pad2(m)}`);
    }
  }
  return opts;
})();

/**
 * Custom saat seçici (açılır-kapanır).
 *
 * @param {string}   value         - Seçili saat "HH:mm"
 * @param {function} onChange       - (timeStr) => void
 * @param {string}   [startTime]   - Çalışma başlangıç saati (yoksa 08:00)
 * @param {string}   [endTime]     - Çalışma bitiş saati (yoksa 18:00)
 * @param {boolean}  [isClosed]    - Gün kapalı mı?
 * @param {string}   [minTime]     - Minimum seçilebilir saat
 * @param {string}   [maxTime]     - Maximum seçilebilir saat
 * @param {string[]} [blockedSlots]- Dolu slotlar
 * @param {string}   [infoText]    - Ek bilgi metni
 * @param {boolean}  [onlyValid]   - true ise sadece seçilebilir slotları göster
 * @param {string}   [label]       - Etiket
 */
export default function TrTimePicker({
  value,
  onChange,
  startTime,
  endTime,
  isClosed = false,
  minTime,
  maxTime,
  blockedSlots = [],
  infoText,
  onlyValid = false,
  label,
}) {
  const [open, setOpen] = useState(false);
  const gridRef = useRef(null);

  const effectiveStart = startTime || DEFAULT_START;
  const effectiveEnd = endTime || DEFAULT_END;
  const hasSchedule = !!startTime && !!endTime;

  const blockedSet = useMemo(() => new Set(blockedSlots), [blockedSlots]);

  const slots = useMemo(() => {
    return ALL_SLOTS.map((t) => {
      const inWorking = t >= effectiveStart && t <= effectiveEnd;
      const aboveMin = minTime ? t >= minTime : true;
      const belowMax = maxTime ? t <= maxTime : true;
      const isBlocked = blockedSet.has(t);
      const isClickable = !isClosed && inWorking && aboveMin && belowMax && !isBlocked;
      return {
        time: t,
        inWorking,
        isClickable,
        isBlocked,
        isSelected: t === value,
      };
    });
  }, [effectiveStart, effectiveEnd, isClosed, minTime, maxTime, blockedSet, value]);

  // Görünür slotlar
  const visibleSlots = useMemo(() => {
    if (onlyValid) {
      // Sadece seçilebilir slotları göster
      return slots.filter((s) => s.isClickable || s.isSelected);
    }
    // Çalışma aralığı ± 1 saat
    const [sh] = (minTime || effectiveStart).split(":").map(Number);
    const [eh] = (maxTime || effectiveEnd).split(":").map(Number);
    const rangeStart = `${pad2(Math.max(0, sh - 1))}:00`;
    const rangeEnd = `${pad2(Math.min(23, eh + 1))}:45`;
    return slots.filter((s) => s.time >= rangeStart && s.time <= rangeEnd);
  }, [slots, effectiveStart, effectiveEnd, minTime, maxTime, onlyValid]);

  // Açıldığında seçili elemana scroll
  useEffect(() => {
    if (open && gridRef.current && value) {
      requestAnimationFrame(() => {
        const el = gridRef.current?.querySelector(`[data-time="${value}"]`);
        if (el) {
          el.scrollIntoView({ block: "center", behavior: "smooth" });
        }
      });
    }
  }, [open, value]);

  const handleSelect = (time) => {
    onChange(time);
    setOpen(false);
  };

  return (
    <Box sx={{ width: "100%" }}>
      {/* Kompakt gösterim */}
      <Box
        onClick={() => setOpen((o) => !o)}
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 1.5,
          p: 1.5,
          border: "1px solid",
          borderColor: open ? "#1976d2" : COLORS.slate200,
          borderRadius: 2,
          cursor: "pointer",
          bgcolor: "white",
          transition: "all 0.15s ease",
          "&:hover": { borderColor: "#1976d2", bgcolor: COLORS.slate50 },
        }}
      >
        <ClockIcon sx={{ color: open ? "#1976d2" : COLORS.slate400, fontSize: 20 }} />
        <Box sx={{ flex: 1, minWidth: 0 }}>
          {label && (
            <Typography sx={{ fontSize: 11, color: COLORS.slate400, fontWeight: 600, lineHeight: 1 }}>
              {label}
            </Typography>
          )}
          <Typography
            sx={{
              fontSize: 15,
              fontWeight: value ? 800 : 400,
              color: value ? COLORS.slate800 : COLORS.slate400,
              fontFamily: value ? "'Roboto Mono', monospace" : undefined,
              lineHeight: 1.4,
            }}
          >
            {value || "Saat seçin"}
          </Typography>
        </Box>
        <ChevronRightIcon
          sx={{
            color: COLORS.slate400,
            fontSize: 18,
            transform: open ? "rotate(90deg)" : "rotate(0deg)",
            transition: "transform 0.2s ease",
          }}
        />
      </Box>

      {/* Açılır panel */}
      <Collapse in={open} timeout={200}>
        <Box
          sx={{
            mt: 1,
            border: `1px solid ${COLORS.slate200}`,
            borderRadius: 3,
            overflow: "hidden",
            bgcolor: "white",
            p: 1,
          }}
        >
          {/* Bilgi satırları */}
          {!isClosed && (
            <Box sx={{ display: "flex", alignItems: "center", gap: 0.8, mb: 0.8, px: 0.3 }}>
              <Box sx={{ width: 5, height: 5, borderRadius: "50%", bgcolor: COLORS.success, flexShrink: 0 }} />
              <Typography sx={{ fontSize: 10, color: COLORS.slate500, fontWeight: 600 }}>
                {hasSchedule
                  ? `Çalışma: ${effectiveStart} – ${effectiveEnd}`
                  : `Varsayılan: ${effectiveStart} – ${effectiveEnd}`}
              </Typography>
            </Box>
          )}

          {isClosed && (
            <Box sx={{ display: "flex", alignItems: "center", gap: 0.8, mb: 0.8, px: 0.3 }}>
              <Box sx={{ width: 5, height: 5, borderRadius: "50%", bgcolor: COLORS.error, flexShrink: 0 }} />
              <Typography sx={{ fontSize: 10, color: COLORS.error, fontWeight: 600 }}>Bu gün kapalı</Typography>
            </Box>
          )}

          {infoText && (
            <Typography sx={{ fontSize: 10, color: COLORS.warning, fontWeight: 600, mb: 0.8, px: 0.3 }}>
              ⚠ {infoText}
            </Typography>
          )}

          {/* Saat grid */}
          <Box
            ref={gridRef}
            sx={{
              display: "grid",
              gridTemplateColumns: "repeat(4, 1fr)",
              gap: "3px",
              maxHeight: 180,
              overflowY: "auto",
              "&::-webkit-scrollbar": { width: 3 },
              "&::-webkit-scrollbar-thumb": { bgcolor: COLORS.slate300, borderRadius: 2 },
            }}
          >
            {visibleSlots.map(({ time, inWorking, isClickable, isBlocked, isSelected }) => {
              let bgColor = "white";
              let textColor = COLORS.slate800;
              let border = `1px solid ${COLORS.slate200}`;
              let cursor = "pointer";
              let fontWeight = 500;
              let hoverBg = COLORS.slate100;

              if (isSelected) {
                bgColor = "#1976d2";
                textColor = "white";
                border = "1px solid #1976d2";
                fontWeight = 800;
                hoverBg = "#1565c0";
              } else if (isBlocked) {
                bgColor = COLORS.errorLightest;
                textColor = "#fca5a5";
                border = "1px solid #fecaca";
                cursor = "not-allowed";
                hoverBg = COLORS.errorLightest;
              } else if (!isClickable) {
                bgColor = COLORS.slate50;
                textColor = "#d1d5db";
                border = `1px solid ${COLORS.slate100}`;
                cursor = "not-allowed";
                hoverBg = COLORS.slate50;
              } else if (inWorking) {
                bgColor = "#f0fdf4";
                border = "1px solid #bbf7d0";
                textColor = COLORS.successDarker;
                fontWeight = 600;
              }

              return (
                <Box
                  key={time}
                  data-time={time}
                  onClick={() => isClickable && handleSelect(time)}
                  title={isBlocked ? "Dolu - Randevu var" : ""}
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    py: 0.6,
                    borderRadius: 1.5,
                    cursor,
                    bgcolor: bgColor,
                    border,
                    transition: "all 0.1s ease",
                    "&:hover": {
                      bgcolor: isClickable ? hoverBg : bgColor,
                      ...(isClickable && !isSelected ? { borderColor: "#1976d2" } : {}),
                    },
                  }}
                >
                  <Typography
                    sx={{
                      fontSize: 12,
                      fontWeight,
                      color: textColor,
                      fontFamily: "'Roboto Mono', monospace",
                    }}
                  >
                    {time}
                  </Typography>
                </Box>
              );
            })}
          </Box>
        </Box>
      </Collapse>
    </Box>
  );
}
